import { useState, useEffect } from "react";
import VideoMaterial from "./VideoMaterial";
import { getAssetDownloadUrl } from "../../services/assetsApi";
import { config } from "../../config/environment";

/**
 * Resolves a direct S3 URL to a presigned download URL via the API.
 * The assets bucket is private, so direct URLs return 403.
 */
function usePresignedVideoUrl(src) {
  const [resolvedUrl, setResolvedUrl] = useState(null);

  useEffect(() => {
    if (!src) return;

    const s3Base = config.assets.s3BaseUrl;
    if (!src.startsWith(s3Base)) {
      // Not an S3 asset URL (e.g. external URL) — use as-is
      setResolvedUrl(src);
      return;
    }

    // Parse roomId and filename from: {s3Base}/rooms/{roomId}/assets/{filename}
    const path = src.substring(s3Base.length + 1);
    const match = path.match(/^rooms\/([^/]+)\/assets\/(.+)$/);
    if (!match) {
      setResolvedUrl(src);
      return;
    }

    const [, roomId, filename] = match;
    let mounted = true;

    getAssetDownloadUrl(roomId, filename)
      .then((url) => { if (mounted) setResolvedUrl(url); })
      .catch((err) => {
        console.warn('[DecoratedVideo] Failed to get presigned URL, falling back:', err.message);
        if (mounted) setResolvedUrl(src);
      });

    return () => { mounted = false; };
  }, [src]);

  return resolvedUrl;
}

function DecoratedVideo({ decor, edges = <></>, disableRaycast = false }) {
  const { width, height, option } = decor;
  const raycastProps = disableRaycast ? { raycast: () => null } : {};
  const videoSrc = usePresignedVideoUrl(option);

  return (
    <mesh {...raycastProps}>
      <planeGeometry args={[width, height]} />
      <VideoMaterial src={videoSrc} />
      {edges}
    </mesh>
  );
}

export default DecoratedVideo;
